import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ApolloError } from "@apollo/client/errors";
import { Router } from "@angular/router";
import { EMPTY } from "rxjs";
import { catchError, first, tap } from "rxjs/operators";
import {
  clearMfaCooldown,
  normalizeMfaEmail,
  PENDING_MFA_STORAGE_KEY,
  PendingMfaState,
  readMfaCooldown,
  writeMfaCooldown,
} from "src/auth/pending-mfa-state";
import { AuthNewService } from "src/auth/services/auth-new.service";
import { VerifyMfaOtp_verifyMfaOtp } from "src/models/phoenix-auth";
import { ToastrService } from "ngx-toastr";

type GraphQlErrorWithExtensions = {
  message?: string;
  extensions?: {
    code?: string;
    cooldown?: number;
  };
};

type ApolloNetworkErrorWithResult = Error & {
  errors?: GraphQlErrorWithExtensions[];
  result?: {
    errors?: GraphQlErrorWithExtensions[];
  };
};

@Component({
  selector: "app-verify-otp",
  templateUrl: "./verify-otp.component.html",
  styleUrls: ["./verify-otp.component.scss"],
})
export class VerifyOtpComponent implements OnInit, AfterViewInit, OnDestroy {
  loading = false;
  resending = false;
  errorMessage: string;
  cooldownRemainingSeconds: number | null = null;
  otpId: string;
  userId: number;
  email: string | null = null;
  redirectUrl: string | null;
  readonly codeLength = 6;
  readonly otpIndexes = Array.from(
    { length: this.codeLength },
    (_, index) => index,
  );
  otpValues = Array.from({ length: this.codeLength }, () => "");
  maskedEmail: string | null = null;

  @ViewChildren("otpInput")
  otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  @ViewChild("verifyButton")
  verifyButton!: ElementRef<HTMLButtonElement>;

  form: UntypedFormGroup;
  private cooldownTimerId: ReturnType<typeof setInterval> | null = null;
  private throttleErrorActive = false;

  constructor(
    private fb: UntypedFormBuilder,
    private auth: AuthNewService,
    private router: Router,
    private toastr: ToastrService,
  ) {
    this.form = this.fb.group({
      code: [
        "",
        [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(6),
          Validators.pattern(/^\d{6}$/),
        ],
      ],
    });

    this.restorePendingMfaState();
  }

  get isDisabled() {
    return this.form.controls.code?.invalid || this.throttleErrorActive;
  }

  get isCooldownActive() {
    return this.throttleErrorActive;
  }

  get cooldownTimeRemaining() {
    if (!this.throttleErrorActive || this.cooldownRemainingSeconds === null) {
      return null;
    }

    return this.formatCountdown(this.cooldownRemainingSeconds);
  }

  ngOnInit(): void {
    if (!this.otpId || !this.userId) {
      this.clearPendingMfa();
      this.router.navigate(["/auth/sign-in"]);
      return;
    }

    this.writeCodeToInputs(this.form.controls.code.value || "");
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.focusDigit(0));
  }

  ngOnDestroy(): void {
    this.clearCooldownTimer();
  }

  onDigitInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const inputEvent = event as InputEvent;
    const digits = this.sanitizeDigits(input.value);

    if (!digits) {
      this.setDigit(index, "");
      return;
    }

    if (
      inputEvent.inputType === "insertFromPaste" ||
      (index === 0 && digits.length === this.codeLength)
    ) {
      this.fillDigits(index, digits);
      return;
    }

    this.setDigit(index, digits.slice(-1));

    this.focusAfterDigitEntry(index);
  }

  onDigitKeydown(event: KeyboardEvent, index: number) {
    if (event.key === "Backspace") {
      event.preventDefault();
      this.handleBackspace(index);
      return;
    }

    if (event.key === "Delete") {
      event.preventDefault();
      this.setDigit(index, "");
      return;
    }

    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      this.focusDigit(index - 1);
      return;
    }

    if (event.key === "ArrowRight" && index < this.codeLength - 1) {
      event.preventDefault();
      this.focusDigit(index + 1);
      return;
    }

    if (event.key === "Home") {
      event.preventDefault();
      this.focusDigit(0);
      return;
    }

    if (event.key === "End") {
      event.preventDefault();
      this.focusDigit(this.codeLength - 1);
      return;
    }

    if (event.ctrlKey || event.metaKey || event.altKey || event.key === "Tab") {
      return;
    }

    if (event.key.length !== 1) {
      return;
    }

    event.preventDefault();

    if (!/\d/.test(event.key)) {
      return;
    }

    this.setDigit(index, event.key);

    this.focusAfterDigitEntry(index);
  }

  onDigitFocus(index: number) {
    const input = this.otpInputs.get(index)?.nativeElement;
    input?.select();
  }

  onPaste(event: ClipboardEvent, index: number) {
    event.preventDefault();
    const pasted = event.clipboardData?.getData("text") || "";
    this.fillDigits(index, pasted);
  }

  onSubmit() {
    if (this.form.invalid) return;

    const { code } = this.form.value;

    this.loading = true;

    this.auth
      .verifyOtp({
        otpId: this.otpId,
        userId: this.userId,
        code,
      })
      .pipe(
        first(),
        tap((auth: VerifyMfaOtp_verifyMfaOtp) => {
          this.clearThrottleError(true);
          this.clearPendingMfa();

          const role = auth.payload.role;
          if (this.redirectUrl) {
            this.router.navigate([this.redirectUrl], {
              state: { preventLoader: true },
            });
          } else if (role === "Admin") {
            this.router.navigate(["/app/expenses/expenses"], {
              state: { preventLoader: true },
            });
          } else if (role === "Analyst") {
            this.router.navigate(["/app/expenses/expenses"], {
              state: { preventLoader: true },
            });
          } else if (role === "User") {
            this.router.navigate(["/app/dashboard"], {
              state: { preventLoader: true },
            });
          } else {
            this.router.navigate(["/auth/sign-in"], {
              state: { preventLoader: true },
            });
          }
        }),
        catchError((error: unknown) => {
          this.applyRequestError(
            error,
            "Invalid verification code. Please try again.",
          );
          this.loading = false;
          return EMPTY;
        }),
      )
      .subscribe();
  }

  onResend() {
    if (this.resending) return;

    this.resending = true;
    if (!this.throttleErrorActive) {
      this.errorMessage = undefined;
    }

    this.auth
      .resendOtp({
        otpId: this.otpId,
        userId: this.userId,
      })
      .pipe(
        first(),
        tap((response) => {
          this.otpId = response.otpId;
          this.userId = response.userId;
          this.clearThrottleError(true);
          this.writePendingMfa(this.buildPendingMfaState());
          this.resetOtpState();
          this.toastr.success("Verification code was sent successfully");
          this.resending = false;
        }),
        catchError((error: unknown) => {
          this.applyRequestError(error, "Failed to resend code.");
          this.resending = false;
          return EMPTY;
        }),
      )
      .subscribe();
  }

  private sanitizeDigits(value: string) {
    return (value || "").replace(/\D/g, "").slice(0, this.codeLength);
  }

  onBackToLogin() {
    this.clearThrottleError(true, false);
    this.clearPendingMfa();
    this.router.navigate(["/auth/sign-in"]);
  }

  private handleBackspace(index: number) {
    if (this.otpValues[index]) {
      this.setDigit(index, "");
      return;
    }

    if (index === 0) {
      return;
    }

    this.setDigit(index - 1, "");
    this.focusDigit(index - 1);
  }

  private setDigit(index: number, value: string) {
    this.otpValues[index] = value;
    this.syncCodeControl();
  }

  private fillDigits(startIndex: number, value: string) {
    const digits = this.sanitizeDigits(value);
    if (!digits) {
      return;
    }

    let nextIndex = startIndex;
    for (const digit of digits) {
      if (nextIndex >= this.codeLength) {
        break;
      }
      this.otpValues[nextIndex] = digit;
      nextIndex++;
    }

    this.syncCodeControl();

    if (nextIndex < this.codeLength) {
      this.focusDigit(nextIndex);
      return;
    }

    this.focusDigit(this.codeLength - 1);
  }

  private writeCodeToInputs(code: string) {
    const digits = this.sanitizeDigits(code);

    for (let index = 0; index < this.codeLength; index++) {
      this.otpValues[index] = digits[index] || "";
    }

    this.syncCodeControl();
  }

  private syncCodeControl() {
    const code = this.otpValues.join("");

    if (this.form.controls.code.value !== code) {
      this.form.controls.code.setValue(code, { emitEvent: false });
    }

    if (!this.throttleErrorActive) {
      this.errorMessage = undefined;
    }
  }

  private focusAfterDigitEntry(index: number) {
    if (index < this.codeLength - 1) {
      this.focusDigit(index + 1);
      return;
    }

    if (this.otpValues.every((value) => !!value)) {
      setTimeout(() => this.focusVerifyButton());
    }
  }

  private resetOtpState() {
    this.form.reset({ code: "" });
    this.writeCodeToInputs("");
    setTimeout(() => this.focusDigit(0));
  }

  private clearOtpState() {
    this.form.reset({ code: "" });
    this.writeCodeToInputs("");
  }

  private focusVerifyButton() {
    const button = this.verifyButton?.nativeElement;
    if (button && !button.disabled) {
      button.focus();
    }
  }

  private focusDigit(index: number) {
    const targetIndex = Math.min(Math.max(index, 0), this.codeLength - 1);
    const input = this.otpInputs?.get(targetIndex)?.nativeElement;
    if (input) {
      input.focus();
      input.select();
    }
  }

  private restorePendingMfaState() {
    const nav = this.router.getCurrentNavigation();
    const state = nav?.extras?.state as Partial<PendingMfaState> | undefined;

    if (state?.otpId && typeof state.userId === "number") {
      const pendingMfaState: PendingMfaState = {
        otpId: state.otpId,
        userId: state.userId,
        email: normalizeMfaEmail(state.email),
        redirectUrl: state.redirectUrl || null,
        maskedEmail: state.maskedEmail || null,
      };

      this.applyPendingMfaState(pendingMfaState);
      this.writePendingMfa(this.buildPendingMfaState());
      this.restoreCooldownFromPendingState();
      return;
    }

    const storedState = this.readPendingMfa();
    if (storedState) {
      this.applyPendingMfaState(storedState);
      this.restoreCooldownFromPendingState();
    }
  }

  private applyPendingMfaState(state: PendingMfaState) {
    this.otpId = state.otpId;
    this.userId = state.userId;
    this.email = normalizeMfaEmail(state.email);
    this.redirectUrl = state.redirectUrl;
    this.maskedEmail = state.maskedEmail;
  }

  private readPendingMfa(): PendingMfaState | null {
    const value = sessionStorage.getItem(PENDING_MFA_STORAGE_KEY);

    if (!value) {
      return null;
    }

    try {
      const state = JSON.parse(value) as Partial<PendingMfaState>;

      if (!state?.otpId || typeof state.userId !== "number") {
        this.clearPendingMfa();
        return null;
      }

      return {
        otpId: state.otpId,
        userId: state.userId,
        email: normalizeMfaEmail(state.email),
        redirectUrl: state.redirectUrl || null,
        maskedEmail: state.maskedEmail || null,
      };
    } catch {
      this.clearPendingMfa();
      return null;
    }
  }

  private buildPendingMfaState(): PendingMfaState {
    return {
      otpId: this.otpId,
      userId: this.userId,
      email: normalizeMfaEmail(this.email),
      redirectUrl: this.redirectUrl || null,
      maskedEmail: this.maskedEmail || null,
    };
  }

  private writePendingMfa(state: PendingMfaState) {
    sessionStorage.setItem(PENDING_MFA_STORAGE_KEY, JSON.stringify(state));
  }

  private clearPendingMfa() {
    sessionStorage.removeItem(PENDING_MFA_STORAGE_KEY);
  }

  private getPrimaryGraphQlError(
    error: unknown,
  ): GraphQlErrorWithExtensions | null {
    if (!(error instanceof ApolloError)) {
      return null;
    }

    if (error.graphQLErrors.length > 0) {
      return error.graphQLErrors[0] as GraphQlErrorWithExtensions;
    }

    const networkError =
      error.networkError as ApolloNetworkErrorWithResult | null;
    const networkErrors = networkError?.result?.errors || networkError?.errors;

    if (Array.isArray(networkErrors) && networkErrors.length > 0) {
      return networkErrors[0];
    }

    return null;
  }

  private applyRequestError(error: unknown, fallbackMessage: string) {
    const cooldown = this.getThrottleCooldown(error);

    if (cooldown !== null && cooldown > 0) {
      this.startCooldown(cooldown);
      return;
    }

    this.clearThrottleError();

    const graphQlError = this.getPrimaryGraphQlError(error);

    this.errorMessage =
      graphQlError?.message || this.getErrorMessage(error) || fallbackMessage;
  }

  private getThrottleCooldown(error: unknown) {
    const cooldown = this.getPrimaryGraphQlError(error)?.extensions?.cooldown;

    return typeof cooldown === "number" ? cooldown : null;
  }

  private getErrorMessage(error: unknown) {
    if (error instanceof Error && error.message) {
      return error.message;
    }

    if (
      error &&
      typeof error === "object" &&
      "message" in error &&
      typeof error.message === "string"
    ) {
      return error.message;
    }

    return undefined;
  }

  private startCooldown(seconds: number) {
    this.clearCooldownTimer();
    this.throttleErrorActive = true;
    const startedAt = Date.now();
    const durationSeconds = Math.max(0, Math.floor(seconds));
    this.clearOtpState();
    writeMfaCooldown({
      userId: this.userId,
      email: this.email,
      startedAt,
      durationSeconds,
    });
    this.setCooldownSeconds(
      this.getRemainingCooldownSeconds(startedAt, durationSeconds),
    );
    this.writePendingMfa(this.buildPendingMfaState());

    this.cooldownTimerId = setInterval(() => {
      const remainingSeconds = this.getStoredRemainingCooldownSeconds();

      if (remainingSeconds === null) {
        this.clearCooldownTimer();
        return;
      }

      if (remainingSeconds <= 0) {
        this.clearThrottleError(true);
        return;
      }

      this.setCooldownSeconds(remainingSeconds);
    }, 1000);
  }

  private setCooldownSeconds(seconds: number | null) {
    if (seconds === null) {
      return;
    }

    const sanitizedSeconds = Math.max(0, Math.floor(seconds));

    this.cooldownRemainingSeconds = sanitizedSeconds;
    this.errorMessage = this.buildCooldownErrorMessage(sanitizedSeconds);
  }

  private clearThrottleError(clearMessage = false, clearStoredCooldown = true) {
    this.throttleErrorActive = false;
    this.cooldownRemainingSeconds = null;
    this.clearCooldownTimer();

    if (clearStoredCooldown) {
      clearMfaCooldown(this.userId, this.email);
    }

    this.writePendingMfa(this.buildPendingMfaState());

    if (clearMessage) {
      this.errorMessage = undefined;
    }
  }

  private clearCooldownTimer() {
    if (this.cooldownTimerId !== null) {
      clearInterval(this.cooldownTimerId);
      this.cooldownTimerId = null;
    }
  }

  private buildCooldownErrorMessage(seconds: number) {
    const minutes = Math.ceil(seconds / 60);

    return `Too many attempts. Please try again in ${minutes} minute${minutes === 1 ? "" : "s"}.`;
  }

  private getRemainingCooldownSeconds(
    startedAt: number | null,
    durationSeconds: number | null,
  ) {
    if (startedAt === null || durationSeconds === null) {
      return null;
    }

    const elapsedSeconds = Math.floor((Date.now() - startedAt) / 1000);

    return Math.max(durationSeconds - elapsedSeconds, 0);
  }

  private getStoredRemainingCooldownSeconds() {
    const cooldown = readMfaCooldown(this.userId, this.email);

    if (!cooldown) {
      return null;
    }

    return this.getRemainingCooldownSeconds(
      cooldown.startedAt,
      cooldown.durationSeconds,
    );
  }

  private restoreCooldownFromPendingState() {
    const cooldown = readMfaCooldown(this.userId, this.email);

    if (!cooldown) {
      return;
    }

    const remainingSeconds = this.getRemainingCooldownSeconds(
      cooldown.startedAt,
      cooldown.durationSeconds,
    );

    if (remainingSeconds === null) {
      return;
    }

    if (remainingSeconds <= 0) {
      this.clearThrottleError(true);
      return;
    }

    this.clearCooldownTimer();
    this.throttleErrorActive = true;
    this.clearOtpState();
    this.setCooldownSeconds(remainingSeconds);

    this.cooldownTimerId = setInterval(() => {
      const nextRemainingSeconds = this.getStoredRemainingCooldownSeconds();

      if (nextRemainingSeconds === null || nextRemainingSeconds <= 0) {
        this.clearThrottleError(true);
        return;
      }

      this.setCooldownSeconds(nextRemainingSeconds);
    }, 1000);
  }

  private formatCountdown(seconds: number) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  }
}
